import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  ArrowRight, Calendar, Clock, User, Tag, Search, Filter, 
  BookOpen, TrendingUp, MessageCircle, Share2, Heart, 
  Eye, ChevronLeft, ChevronRight 
} from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Footer from "../../components/layout/Footer";
import { useTheme } from "../../components/ThemeContext.tsx";

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;
  const { isDarkMode } = useTheme();

  const categories = [
    { id: 'all', name: 'All Posts', count: 24 },
    { id: 'industry-insights', name: 'Industry Insights', count: 8 },
    { id: 'technology', name: 'Technology', count: 6 },
    { id: 'case-studies', name: 'Case Studies', count: 4 },
    { id: 'best-practices', name: 'Best Practices', count: 3 },
    { id: 'market-trends', name: 'Market Trends', count: 3 }
  ];

  const blogPosts = [
    {
      id: 1,
      title: "The Future of AI in Renewable Energy Operations",
      excerpt: "Exploring how artificial intelligence is revolutionizing renewable energy management, from predictive maintenance to optimal energy dispatch.",
      category: 'technology',
      author: {
        name: "Dr. Sarah Mitchell",
        avatar: "/assets/img/general/steptodown.com151755.jpg",
        role: "Chief AI Officer"
      },
      publishDate: "2024-03-15",
      readTime: "8 min read",
      image: "/assets/img/general/ai-generated-8685407_1280.jpg",
      tags: ['AI', 'Machine Learning', 'Predictive Analytics', 'Energy Management'],
      views: 2847,
      likes: 156,
      comments: 23,
      featured: true
    },
    {
      id: 2,
      title: "Optimizing Solar Farm Performance: Best Practices Guide",
      excerpt: "A comprehensive guide to maximizing solar farm efficiency through proper monitoring, maintenance, and performance optimization strategies.",
      category: 'best-practices',
      author: {
        name: "Michael Rodriguez",
        avatar: "/assets/img/general/steptodown.com188081.jpg",
        role: "Senior Solar Engineer"
      },
      publishDate: "2024-03-12",
      readTime: "12 min read",
      image: "/assets/img/general/photovoltaic-system-2742302_1280.jpg",
      tags: ['Solar Energy', 'Optimization', 'Maintenance', 'Performance'],
      views: 3241,
      likes: 189,
      comments: 34,
      featured: false
    },
    {
      id: 3,
      title: "Wind Energy Market Trends: 2024 Outlook",
      excerpt: "Analysis of current wind energy market trends, including offshore developments, technology advances, and investment opportunities.",
      category: 'market-trends',
      author: {
        name: "Emma Thompson",
        avatar: "/assets/img/general/steptodown.com274154.jpg",
        role: "Market Analyst"
      },
      publishDate: "2024-03-10",
      readTime: "6 min read",
      image: "/assets/img/general/wind-power-generation-8370867_1280.jpg",
      tags: ['Wind Energy', 'Market Analysis', 'Investment', 'Offshore'],
      views: 1923,
      likes: 98,
      comments: 15,
      featured: false
    },
    {
      id: 4,
      title: "Energy Storage: The Key to Renewable Energy Grid Integration",
      excerpt: "Understanding how battery storage systems are enabling higher renewable energy penetration and grid stability.",
      category: 'industry-insights',
      author: {
        name: "David Chen",
        avatar: "/assets/img/general/steptodown.com151755.jpg",
        role: "Grid Integration Specialist"
      },
      publishDate: "2024-03-08",
      readTime: "10 min read",
      image: "/assets/img/general/solar-cells-4518932_1280.jpg",
      tags: ['Energy Storage', 'Grid Integration', 'Battery Technology', 'Renewable Energy'],
      views: 2156,
      likes: 134,
      comments: 28,
      featured: true
    },
    {
      id: 5,
      title: "Predictive Maintenance: Reducing Downtime in Wind Turbines",
      excerpt: "How predictive maintenance technologies are transforming wind farm operations and reducing unplanned downtime.",
      category: 'technology',
      author: {
        name: "Lisa Anderson",
        avatar: "/assets/img/general/steptodown.com188081.jpg",
        role: "Maintenance Technology Lead"
      },
      publishDate: "2024-03-05",
      readTime: "7 min read",
      image: "/assets/img/general/windmills-1838788_1280.jpg",
      tags: ['Predictive Maintenance', 'Wind Energy', 'IoT', 'Sensors'],
      views: 1784,
      likes: 112,
      comments: 19,
      featured: false
    },
    {
      id: 6,
      title: "Case Study: 500MW Solar Farm Efficiency Improvement",
      excerpt: "Real-world case study showing how AI-driven optimization increased solar farm efficiency by 28% in 12 months.",
      category: 'case-studies',
      author: {
        name: "Alex Kumar",
        avatar: "/assets/img/general/steptodown.com274154.jpg",
        role: "Project Manager"
      },
      publishDate: "2024-03-03",
      readTime: "15 min read",
      image: "/assets/img/general/photovoltaic-2814504_1280.jpg",
      tags: ['Case Study', 'Solar Energy', 'AI Optimization', 'Efficiency'],
      views: 2987,
      likes: 201,
      comments: 42,
      featured: false
    },
    {
      id: 7,
      title: "The Role of Digital Twins in Renewable Energy",
      excerpt: "Exploring how digital twin technology is revolutionizing renewable energy asset management and optimization.",
      category: 'technology',
      author: {
        name: "Dr. Rachel Kim",
        avatar: "/assets/img/general/steptodown.com151755.jpg",
        role: "Digital Innovation Director"
      },
      publishDate: "2024-03-01",
      readTime: "9 min read",
      image: "/assets/img/general/solar-energy-7882650_1280.jpg",
      tags: ['Digital Twins', 'Asset Management', 'Innovation', 'Technology'],
      views: 1542,
      likes: 87,
      comments: 16,
      featured: false
    },
    {
      id: 8,
      title: "Regulatory Landscape for Renewable Energy in 2024",
      excerpt: "Comprehensive overview of regulatory changes and policy developments affecting renewable energy projects globally.",
      category: 'industry-insights',
      author: {
        name: "James Wilson",
        avatar: "/assets/img/general/steptodown.com188081.jpg",
        role: "Policy Analyst"
      },
      publishDate: "2024-02-28",
      readTime: "11 min read",
      image: "/assets/img/general/solar-6816101_1280.jpg",
      tags: ['Regulation', 'Policy', 'Compliance', 'Industry'],
      views: 2234,
      likes: 145,
      comments: 31,
      featured: true
    }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  const featuredPost = blogPosts.find(post => post.featured) || blogPosts[0];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <Layout header={6}>
      <div className={`min-h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
        {/* Hero Section */}
        <section className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <span className="inline-block bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Industry Blog
              </span>
              <h1 className={`text-4xl lg:text-6xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Insights & Innovation
              </h1>
              <p className={`text-xl max-w-3xl mx-auto mb-8 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Stay ahead of the curve with expert insights, industry trends, 
                and innovative solutions in renewable energy technology.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Featured Post */}
        <section className={`py-12 ${
          isDarkMode ? 'bg-slate-800' : 'bg-white'
        }`}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 mb-8">
              <BookOpen className="w-6 h-6 text-blue-500" />
              <h2 className={`text-2xl font-bold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Featured Article
              </h2>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
            >
              <div className="relative overflow-hidden rounded-2xl">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title}
                  className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium capitalize">
                    {featuredPost.category.replace('-', ' ')}
                  </span>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className={`text-3xl font-bold mb-4 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {featuredPost.title}
                  </h3>
                  <p className={`text-lg ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {featuredPost.excerpt}
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <img 
                    src={featuredPost.author.avatar} 
                    alt={featuredPost.author.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className={`font-semibold ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {featuredPost.author.name}
                    </p>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {featuredPost.author.role}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6 text-sm">
                  <span className={`flex items-center gap-2 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Calendar className="w-4 h-4" />
                    {formatDate(featuredPost.publishDate)}
                  </span>
                  <span className={`flex items-center gap-2 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Clock className="w-4 h-4" />
                    {featuredPost.readTime}
                  </span>
                  <span className={`flex items-center gap-2 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    <Eye className="w-4 h-4" />
                    {featuredPost.views.toLocaleString()} views
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {featuredPost.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        isDarkMode 
                          ? 'bg-slate-700 text-gray-300' 
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/blog/${featuredPost.id}`}
                  className="inline-flex items-center gap-2 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
                  style={{ background: 'linear-gradient(to right, #1C4D88, #0084CF)' }}
                  onMouseEnter={(e) => e.target.style.background = 'linear-gradient(to right, #0084CF, #1C4D88)'}
                  onMouseLeave={(e) => e.target.style.background = 'linear-gradient(to right, #1C4D88, #0084CF)'}
                >
                  Read Full Article
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Filters and Search */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row gap-6 items-center justify-between mb-8">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`} />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300`}
                />
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                      selectedCategory === category.id
                        ? 'text-white shadow-lg'
                        : isDarkMode
                          ? 'bg-slate-800 text-gray-300 hover:bg-slate-700'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                    style={selectedCategory === category.id ? { background: 'linear-gradient(to right, #1C4D88, #0084CF)' } : {}}
                  >
                    {category.name}
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      selectedCategory === category.id
                        ? 'bg-white/20'
                        : isDarkMode
                          ? 'bg-slate-700'
                          : 'bg-gray-100'
                    }`}>
                      {category.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {currentPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className={`rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 ${
                    isDarkMode ? 'bg-slate-800' : 'bg-white'
                  } group`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium capitalize">
                        {post.category.replace('-', ' ')}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-3">
                      <img 
                        src={post.author.avatar} 
                        alt={post.author.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div>
                        <p className={`text-sm font-medium ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {post.author.name}
                        </p>
                        <p className={`text-xs ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {post.author.role}
                        </p>
                      </div>
                    </div>

                    <h3 className={`text-lg font-bold mb-3 group-hover:text-blue-500 transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {post.title}
                    </h3>
                    
                    <p className={`text-sm mb-4 line-clamp-3 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {post.excerpt}
                    </p>

                    <div className="flex items-center gap-4 text-xs mb-4">
                      <span className={`flex items-center gap-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.publishDate)}
                      </span>
                      <span className={`flex items-center gap-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4 text-xs">
                        <span className={`flex items-center gap-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          <Eye className="w-3 h-3" />
                          {post.views.toLocaleString()}
                        </span>
                        <span className={`flex items-center gap-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          <Heart className="w-3 h-3" />
                          {post.likes}
                        </span>
                        <span className={`flex items-center gap-1 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          <MessageCircle className="w-3 h-3" />
                          {post.comments}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {post.tags.slice(0, 2).map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isDarkMode 
                              ? 'bg-slate-700 text-gray-300' 
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-12">
                <div className={`text-6xl mb-4 ${
                  isDarkMode ? 'text-gray-600' : 'text-gray-300'
                }`}>
                  📝
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  No articles found
                </h3>
                <p className={`${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Try adjusting your search or filter criteria
                </p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-12">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    currentPage === 1
                      ? isDarkMode 
                        ? 'text-gray-600 cursor-not-allowed' 
                        : 'text-gray-400 cursor-not-allowed'
                      : isDarkMode
                        ? 'text-white hover:bg-slate-700'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex items-center gap-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-lg font-medium transition-all duration-300 ${
                        currentPage === page
                          ? 'text-white'
                          : isDarkMode
                            ? 'text-gray-300 hover:bg-slate-700'
                            : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      style={currentPage === page ? { background: 'linear-gradient(to right, #1C4D88, #0084CF)' } : {}}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    currentPage === totalPages
                      ? isDarkMode 
                        ? 'text-gray-600 cursor-not-allowed' 
                        : 'text-gray-400 cursor-not-allowed'
                      : isDarkMode
                        ? 'text-white hover:bg-slate-700'
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Subscription */}
        <section className="py-20" style={{ background: 'linear-gradient(to right, #1C4D88, #0084CF)' }}>
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Stay Updated with Industry Insights
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Subscribe to our newsletter and get the latest renewable energy 
                insights, trends, and expert analysis delivered to your inbox.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-xl border-0 focus:ring-2 focus:ring-white/50 transition-all duration-300"
                />
                <button className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2">
                  Subscribe
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer footer={6} />
    </Layout>
  );
};

export default BlogPage;
